/**
 * Thornhop.ts
 * Arc-jumping enemy for Starfall Courier.
 *
 * Periodically leaps toward Nova in a parabolic arc. Immune to stomp
 * (stomping bounces Nova off instead of killing Thornhop). Killable by
 * Star Shard only.
 *
 * State machine: IDLE → JUMPING → LANDING → IDLE → …
 *
 * Rules (AGENTS.md): No React. No async. No Skia. Extends BaseEntity.
 *
 * Phase 2 — Combat, Enemies, Checkpoints, HUD.
 *
 * @module Thornhop
 */

import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime, NovaRuntime } from '../../engine/gameState';
import { resolveAllPlatforms } from '../../engine/collision';
import { GRAVITY, MAX_FALL_SPEED } from '../../engine/physics';
import Logger from '../../utils/logger';

export type ThornhopState = 'IDLE' | 'JUMPING' | 'LANDING' | 'DEAD';

export const THORNHOP_WIDTH = 22;
export const THORNHOP_HEIGHT = 28;

/** Seconds Thornhop waits on the ground before jumping. */
const IDLE_DURATION = 1.8;
/** Seconds in LANDING state before returning to IDLE. */
const LAND_DURATION = 0.3;
/** Horizontal speed toward Nova during a jump (px/s). */
const JUMP_SPEED_H = 120;
/** Initial upward velocity of the jump (px/s, negative = up). Reduced so it can't snipe Nova on high platforms. */
const JUMP_FORCE = -350;
/** Maximum horizontal range before Thornhop won't bother jumping (px). */
const JUMP_RANGE = 320;

export const THORNHOP_COLOR = '#7a40cc'; // purple

// DECISION: Reusable hitbox for collision resolution to avoid per-tick allocations.
const _hitbox = { x: 0, y: 0, width: THORNHOP_WIDTH, height: THORNHOP_HEIGHT };

/**
 * Thornhop — arc-jumping enemy.
 *
 * Pass `nova` to `update()` each tick so it can aim its jump.
 */
export class Thornhop extends BaseEntity {
  public readonly entityType = 'Thornhop';
  public readonly color = THORNHOP_COLOR;
  /**
   * Measured empty air above Thornhop's art (10.95px of its 28px box). Floored
   * so the inset never eats real pixels. Verified by enemyCombatInset.test.ts.
   */
  public readonly combatTopInset = 10;
  /** True — Thornhop cannot be killed by stomp, only Star Shards. */
  public readonly stompImmune = true;
  public state: ThornhopState = 'IDLE';

  /**
   * True once dead. Thornhop has no intermediate death animation — the
   * fatal hit sets `DEAD` directly. See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'DEAD';
  }
  public facingRight: boolean = false;
  private _timer = IDLE_DURATION; // starts waiting

  constructor(x: number, y: number) {
    super(x, y, THORNHOP_WIDTH, THORNHOP_HEIGHT);
  }

  public update(dt: number, level: LevelRuntime, nova?: NovaRuntime): void {
    if (this.state === 'DEAD') return;

    // ── Gravity ───────────────────────────────────────────────────────────────
    this.vy += GRAVITY * dt;
    this.vy = Math.min(this.vy, MAX_FALL_SPEED);

    // ── Integrate ─────────────────────────────────────────────────────────────
    this.x += this.vx * dt;
    this.y += this.vy * dt;

    // ── Collision ─────────────────────────────────────────────────────────────
    // DECISION: Update reusable hitbox in-place instead of calling getHitbox().
    _hitbox.x = this.x;
    _hitbox.y = this.y;
    const result = resolveAllPlatforms(this.x, this.y, _hitbox, level.platforms, this.vy);
    this.x = result.x;
    this.y = result.y;
    if (result.hitLeft || result.hitRight) this.vx = 0;

    if (result.landedOnTop) {
      this.vy = 0;
      if (this.state === 'JUMPING') {
        this.state = 'LANDING';
        this._timer = LAND_DURATION;
        Logger.debug('Thornhop', 'Landed');
      }
    }

    // ── State timer ───────────────────────────────────────────────────────────
    // DECISION: Only decrement timer while grounded. If Thornhop spawns falling,
    // the timer pauses until it lands, preventing immediate jumps upon landing.
    const isGrounded = this.vy === 0;
    if (isGrounded) {
      this._timer -= dt;
      if (nova) {
        // Face Nova when idling on the ground
        const dx = nova.x - this.x;
        if (dx !== 0) this.facingRight = dx > 0;
      }
    }

    // DECISION: Only allow jump when grounded (vy === 0 after collision resolution).
    // This prevents mid-air jumps if Thornhop spawns falling or is knocked off a ledge.
    if (this.state === 'IDLE' && this._timer <= 0 && nova && isGrounded) {
      const dx = nova.x - this.x;
      if (Math.abs(dx) <= JUMP_RANGE) {
        this.vx = dx > 0 ? JUMP_SPEED_H : -JUMP_SPEED_H;
        this.vy = JUMP_FORCE;
        this.state = 'JUMPING';
        Logger.debug('Thornhop', 'Jump toward Nova');
      } else {
        // Out of range — reset idle timer
        this._timer = IDLE_DURATION;
      }
    }

    if (this.state === 'LANDING' && this._timer <= 0) {
      Logger.debug('Thornhop', 'LANDING -> IDLE');
      this.state = 'IDLE';
      this._timer = IDLE_DURATION;
      this.vx = 0;
    }
  }

  /**
   * Apply a Star Shard hit. Thornhop is immune to stomp — call this only for
   * projectile hits. Stomping is handled in combatStep (bounces Nova).
   * @returns true if the enemy was killed by this hit (false if already dead).
   */
  public applyHit(): boolean {
    if (this.state === 'DEAD') return false;
    this.state = 'DEAD';
    this.dead = true;
    Logger.debug('Thornhop', 'Killed by shard');
    return true;
  }
}
